import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  Mail,
  Shield,
  Globe,
  Calendar,
  Clock,
  Scan,
  Users,
  Gift,
  CreditCard,
  Smartphone,
  Settings2,
  MapPin,
  CheckCircle,
  XCircle,
  Trophy,
  Zap,
  Flame,
  Award,
  MessageSquare,
  StickyNote,
  Plus,
  Trash2,
  ExternalLink,
} from 'lucide-react';
import { useState } from 'react';
import { api } from '../../services/api';
import { UserPermissions } from '../../components/admin/UserPermissions';
import { usePermission, PERMISSIONS } from '../../hooks/usePermission';

// Auth provider labels and colors
const AUTH_PROVIDERS: Record<string, { label: string; color: string; bg: string }> = {
  email: { label: 'Email', color: 'text-gray-700', bg: 'bg-gray-100' },
  google: { label: 'Google', color: 'text-red-700', bg: 'bg-red-50' },
  facebook: { label: 'Facebook', color: 'text-blue-700', bg: 'bg-blue-50' },
  apple: { label: 'Apple', color: 'text-white', bg: 'bg-gray-800' },
};

// Role colors
const ROLE_COLORS: Record<string, string> = {
  super_admin: 'bg-red-100 text-red-700',
  admin: 'bg-primary-100 text-primary-700',
  moderator: 'bg-purple-100 text-purple-700',
  support: 'bg-blue-100 text-blue-700',
  content_manager: 'bg-green-100 text-green-700',
  user: 'bg-gray-100 text-gray-700',
};

const ROLE_LABELS: Record<string, string> = {
  super_admin: 'Super Admin',
  admin: 'Admin',
  moderator: 'Moderator',
  support: 'Support',
  content_manager: 'Content Manager',
  user: 'User',
};

// Tier colors
const TIER_COLORS: Record<string, string> = {
  free: 'bg-gray-100 text-gray-700',
  hunter: 'bg-blue-100 text-blue-700',
  pro: 'bg-green-100 text-green-700',
  'pro+': 'bg-purple-100 text-purple-700',
  dealer: 'bg-amber-100 text-amber-700',
};

// Available tiers for admin override
const ADMIN_TIERS = ['free', 'pro', 'pro+'] as const;

// Ticket status colors
const TICKET_STATUS_COLORS: Record<string, string> = {
  open: 'bg-yellow-100 text-yellow-700',
  in_progress: 'bg-blue-100 text-blue-700',
  resolved: 'bg-green-100 text-green-700',
  closed: 'bg-gray-100 text-gray-700',
};

export default function UserDetail() {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showPermissions, setShowPermissions] = useState(false);
  const [newNote, setNewNote] = useState('');
  const [selectedTier, setSelectedTier] = useState<string | null>(null);
  const canManageRoles = usePermission(PERMISSIONS.USERS_ROLES);

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin-user-detail', userId],
    queryFn: () => api.getAdminUserDetail(userId!),
    enabled: !!userId,
  });

  const addNoteMutation = useMutation({
    mutationFn: (note: string) => api.addAdminUserNote(userId!, note),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-user-detail', userId] });
      setNewNote('');
    },
  });

  const deleteNoteMutation = useMutation({
    mutationFn: (noteId: string) => api.deleteAdminUserNote(userId!, noteId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-user-detail', userId] });
    },
  });

  const setTierMutation = useMutation({
    mutationFn: (tier: 'free' | 'pro' | 'pro+') => api.setUserTier(userId!, tier),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-user-detail', userId] });
      setSelectedTier(null);
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-gray-500">Loading user details...</div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="space-y-4">
        <button
          onClick={() => navigate('/admin/users')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft size={20} />
          Back to Users
        </button>
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          Failed to load user details. The user may not exist.
        </div>
      </div>
    );
  }

  const { user, subscription, tier, scan_stats, recent_scans, referral, gamification, support_tickets, internal_notes } = data;
  const provider = AUTH_PROVIDERS[user.auth_provider || 'email'];

  const handleAddNote = () => {
    if (newNote.trim()) {
      addNoteMutation.mutate(newNote);
    }
  };

  return (
    <div className="space-y-6">
      {/* Back Button */}
      <button
        onClick={() => navigate('/admin/users')}
        className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
      >
        <ArrowLeft size={20} />
        Back to Users
      </button>

      {/* User Header */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-primary-100 flex items-center justify-center text-primary-600 text-2xl font-bold">
              {user.email.charAt(0).toUpperCase()}
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
                {user.email}
                {user.role && user.role !== 'user' && (
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full ${ROLE_COLORS[user.role]}`}>
                    <Shield size={12} />
                    {ROLE_LABELS[user.role]}
                  </span>
                )}
              </h1>
              <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${provider.bg} ${provider.color}`}>
                  {provider.label}
                </span>
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${TIER_COLORS[tier]}`}>
                  {tier.charAt(0).toUpperCase() + tier.slice(1)}
                </span>
                {user.country && (
                  <span className="flex items-center gap-1">
                    <Globe size={14} />
                    {user.country}
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {subscription && (
              <a
                href={`https://dashboard.stripe.com/customers?email=${encodeURIComponent(user.email)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
              >
                <ExternalLink size={16} />
                Stripe
              </a>
            )}
            {canManageRoles && (
              <button
                onClick={() => setShowPermissions(true)}
                className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
              >
                <Settings2 size={18} />
                Manage Permissions
              </button>
            )}
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mt-6 pt-6 border-t border-gray-100">
          <div>
            <div className="text-sm text-gray-500 flex items-center gap-1">
              <Calendar size={14} />
              Joined
            </div>
            <div className="font-medium">{new Date(user.created_at).toLocaleDateString()}</div>
          </div>
          <div>
            <div className="text-sm text-gray-500 flex items-center gap-1">
              <Clock size={14} />
              Last Active
            </div>
            <div className="font-medium">
              {user.last_login ? new Date(user.last_login).toLocaleDateString() : 'Never'}
            </div>
          </div>
          <div>
            <div className="text-sm text-gray-500 flex items-center gap-1">
              <Scan size={14} />
              Total Scans
            </div>
            <div className="font-medium">{scan_stats.total_scans}</div>
          </div>
          <div>
            <div className="text-sm text-gray-500 flex items-center gap-1">
              <Users size={14} />
              Referrals
            </div>
            <div className="font-medium">{referral.total_referrals}</div>
          </div>
          <div>
            <div className="text-sm text-gray-500 flex items-center gap-1">
              <Trophy size={14} />
              Level
            </div>
            <div className="font-medium">{gamification.level}</div>
          </div>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Gamification Card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2 mb-4">
            <Trophy size={20} className="text-amber-500" />
            Gamification
          </h2>
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-amber-50 rounded-lg p-3">
              <div className="flex items-center gap-2 mb-1">
                <Zap size={16} className="text-amber-600" />
                <span className="text-sm text-gray-500">XP</span>
              </div>
              <div className="text-2xl font-bold text-amber-700">{gamification.xp.toLocaleString()}</div>
            </div>
            <div className="bg-purple-50 rounded-lg p-3">
              <div className="flex items-center gap-2 mb-1">
                <Trophy size={16} className="text-purple-600" />
                <span className="text-sm text-gray-500">Level</span>
              </div>
              <div className="text-2xl font-bold text-purple-700">{gamification.level}</div>
            </div>
            <div className="bg-orange-50 rounded-lg p-3">
              <div className="flex items-center gap-2 mb-1">
                <Flame size={16} className="text-orange-600" />
                <span className="text-sm text-gray-500">Streak</span>
              </div>
              <div className="text-2xl font-bold text-orange-700">{gamification.streak_days} days</div>
            </div>
            <div className="bg-blue-50 rounded-lg p-3">
              <div className="flex items-center gap-2 mb-1">
                <Scan size={16} className="text-blue-600" />
                <span className="text-sm text-gray-500">As-Is Scans</span>
              </div>
              <div className="text-2xl font-bold text-blue-700">{gamification.total_as_is_scans}</div>
            </div>
          </div>

          {/* Badges */}
          {gamification.badges.length > 0 && (
            <div className="mt-4 pt-4 border-t border-gray-100">
              <div className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">
                <Award size={14} />
                Badges ({gamification.badges.length})
              </div>
              <div className="flex flex-wrap gap-2">
                {gamification.badges.map((badge) => (
                  <span
                    key={badge.badge_id}
                    className="px-2 py-1 bg-gradient-to-r from-amber-100 to-yellow-100 text-amber-800 rounded-full text-xs font-medium"
                    title={`Earned ${new Date(badge.earned_at).toLocaleDateString()}`}
                  >
                    {badge.badge_name}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Subscription Card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2 mb-4">
            <CreditCard size={20} className="text-gray-400" />
            Subscription
          </h2>
          {subscription ? (
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-500">Tier</span>
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${TIER_COLORS[subscription.tier]}`}>
                  {subscription.tier.charAt(0).toUpperCase() + subscription.tier.slice(1)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Status</span>
                <span className={`font-medium ${subscription.status === 'active' ? 'text-green-600' : 'text-yellow-600'}`}>
                  {subscription.status.charAt(0).toUpperCase() + subscription.status.slice(1)}
                </span>
              </div>
              {subscription.source && (
                <div className="flex justify-between">
                  <span className="text-gray-500">Source</span>
                  <span className="font-medium">{subscription.source}</span>
                </div>
              )}
              {subscription.started_at && (
                <div className="flex justify-between">
                  <span className="text-gray-500">Started</span>
                  <span className="font-medium">{new Date(subscription.started_at).toLocaleDateString()}</span>
                </div>
              )}
              {subscription.expires_at && (
                <div className="flex justify-between">
                  <span className="text-gray-500">Expires</span>
                  <span className="font-medium">{new Date(subscription.expires_at).toLocaleDateString()}</span>
                </div>
              )}
              {subscription.source === 'admin_override' && (
                <div className="mt-2 px-2 py-1 bg-yellow-50 border border-yellow-200 rounded text-xs text-yellow-700">
                  Admin Override
                </div>
              )}
            </div>
          ) : (
            <p className="text-gray-500">No active subscription (Free tier)</p>
          )}

          {/* Admin Tier Override */}
          <div className="mt-4 pt-4 border-t border-gray-200">
            <label htmlFor="override-tier" className="block text-sm font-medium text-gray-700 mb-2">
              Override Tier
            </label>
            <div className="flex gap-2">
              <select
                id="override-tier"
                value={selectedTier ?? (subscription?.tier || tier || 'free')}
                onChange={(e) => setSelectedTier(e.target.value)}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                {ADMIN_TIERS.map((t) => (
                  <option key={t} value={t}>
                    {t.charAt(0).toUpperCase() + t.slice(1)}
                  </option>
                ))}
              </select>
              <button
                onClick={() => {
                  const tierToSet = selectedTier ?? (subscription?.tier || tier || 'free');
                  if (tierToSet !== (subscription?.tier || tier)) {
                    setTierMutation.mutate(tierToSet as 'free' | 'pro' | 'pro+');
                  }
                }}
                disabled={setTierMutation.isPending || (selectedTier === null && !subscription?.tier)}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {setTierMutation.isPending ? 'Saving...' : 'Set Tier'}
              </button>
            </div>
            {setTierMutation.isError && (
              <p className="mt-2 text-sm text-red-600">Failed to update tier. Please try again.</p>
            )}
          </div>
        </div>

        {/* Scan Statistics Card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2 mb-4">
            <Scan size={20} className="text-gray-400" />
            Scan Statistics
          </h2>
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="text-2xl font-bold text-gray-900">{scan_stats.total_scans}</div>
              <div className="text-sm text-gray-500">Total Scans</div>
            </div>
            <div className="bg-green-50 rounded-lg p-3">
              <div className="text-2xl font-bold text-green-700">{scan_stats.verified_scans}</div>
              <div className="text-sm text-gray-500">Verified Scans</div>
            </div>
            <div className="bg-blue-50 rounded-lg p-3">
              <div className="text-2xl font-bold text-blue-700">{scan_stats.unique_products}</div>
              <div className="text-sm text-gray-500">Unique Products</div>
            </div>
            <div className="bg-purple-50 rounded-lg p-3">
              <div className="text-2xl font-bold text-purple-700">{scan_stats.unique_stores}</div>
              <div className="text-sm text-gray-500">Unique Stores</div>
            </div>
          </div>
          <div className="mt-4 pt-4 border-t border-gray-100 grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-gray-500">This Week:</span>
              <span className="ml-2 font-medium">{scan_stats.scans_this_week}</span>
            </div>
            <div>
              <span className="text-gray-500">This Month:</span>
              <span className="ml-2 font-medium">{scan_stats.scans_this_month}</span>
            </div>
          </div>
        </div>

        {/* Referral Card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2 mb-4">
            <Gift size={20} className="text-gray-400" />
            Referrals
          </h2>
          <div className="space-y-4">
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="text-xs text-gray-500 uppercase tracking-wide">Referral Code</div>
              <div className="font-mono font-medium text-lg">{referral.code || 'N/A'}</div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="text-sm text-gray-500">Total Referrals</div>
                <div className="text-xl font-bold">{referral.total_referrals}</div>
              </div>
              <div>
                <div className="text-sm text-gray-500">Successful</div>
                <div className="text-xl font-bold text-green-600">{referral.successful_referrals}</div>
              </div>
            </div>
            {referral.referred_by && (
              <div className="pt-4 border-t border-gray-100">
                <div className="text-sm text-gray-500 mb-2">Referred By</div>
                <div className="flex items-center gap-2">
                  <Mail size={14} className="text-gray-400" />
                  <span className="font-medium">{referral.referred_by.referrer_email}</span>
                </div>
                <div className="text-xs text-gray-400 mt-1">
                  Code: {referral.referred_by.code_used} | {new Date(referral.referred_by.date).toLocaleDateString()}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Extension Info */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2 mb-4">
            <Smartphone size={20} className="text-gray-400" />
            Extension & App
          </h2>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-gray-500">Extension Installed</span>
              {user.extension_installed ? (
                <span className="flex items-center gap-1 text-green-600">
                  <CheckCircle size={16} />
                  Yes
                </span>
              ) : (
                <span className="flex items-center gap-1 text-gray-400">
                  <XCircle size={16} />
                  No
                </span>
              )}
            </div>
            {user.extension_installed_at && (
              <div className="flex items-center justify-between">
                <span className="text-gray-500">Installed On</span>
                <span className="font-medium">{new Date(user.extension_installed_at).toLocaleDateString()}</span>
              </div>
            )}
          </div>
        </div>

        {/* Support Tickets */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2 mb-4">
            <MessageSquare size={20} className="text-gray-400" />
            Support Tickets ({support_tickets.length})
          </h2>
          {support_tickets.length > 0 ? (
            <div className="space-y-3 max-h-64 overflow-y-auto">
              {support_tickets.map((ticket) => (
                <div key={ticket.id} className="border border-gray-100 rounded-lg p-3">
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-medium text-sm">{ticket.subject}</span>
                    <span className={`px-2 py-0.5 text-xs rounded-full ${TICKET_STATUS_COLORS[ticket.status] || 'bg-gray-100'}`}>
                      {ticket.status.replace('_', ' ')}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 text-xs text-gray-500">
                    <span>{ticket.category}</span>
                    <span>•</span>
                    <span>{new Date(ticket.created_at).toLocaleDateString()}</span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500">No support tickets</p>
          )}
        </div>
      </div>

      {/* Internal Notes - Full Width */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2 mb-4">
          <StickyNote size={20} className="text-gray-400" />
          Internal Notes ({internal_notes.length})
        </h2>

        {/* Add Note Form */}
        <div className="flex gap-2 mb-4">
          <input
            type="text"
            value={newNote}
            onChange={(e) => setNewNote(e.target.value)}
            placeholder="Add a note about this user..."
            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            onKeyPress={(e) => e.key === 'Enter' && handleAddNote()}
          />
          <button
            onClick={handleAddNote}
            disabled={!newNote.trim() || addNoteMutation.isPending}
            className="flex items-center gap-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <Plus size={16} />
            Add
          </button>
        </div>

        {/* Notes List */}
        {internal_notes.length > 0 ? (
          <div className="space-y-3">
            {internal_notes.map((note) => (
              <div key={note.id} className="bg-yellow-50 border border-yellow-100 rounded-lg p-3">
                <div className="flex items-start justify-between gap-2">
                  <p className="text-sm text-gray-800">{note.note}</p>
                  <button
                    onClick={() => deleteNoteMutation.mutate(note.id)}
                    className="text-gray-400 hover:text-red-500 transition-colors"
                    title="Delete note"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
                <div className="text-xs text-gray-500 mt-2">
                  {new Date(note.created_at).toLocaleString()}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-500">No internal notes</p>
        )}
      </div>

      {/* Recent Scans */}
      {recent_scans.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Recent Scans</h2>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                  <th className="pb-3 pr-4">Product</th>
                  <th className="pb-3 pr-4">Store</th>
                  <th className="pb-3 pr-4">Type</th>
                  <th className="pb-3 pr-4">Source</th>
                  <th className="pb-3 pr-4">Verified</th>
                  <th className="pb-3">Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {recent_scans.map((scan) => (
                  <tr key={scan.id}>
                    <td className="py-3 pr-4 font-mono text-sm">{scan.product_code}</td>
                    <td className="py-3 pr-4 flex items-center gap-1">
                      <MapPin size={14} className="text-gray-400" />
                      {scan.store_id}
                    </td>
                    <td className="py-3 pr-4">
                      <span className="px-2 py-0.5 text-xs rounded-full bg-gray-100">
                        {scan.scan_type}
                      </span>
                    </td>
                    <td className="py-3 pr-4 text-sm text-gray-500">{scan.source}</td>
                    <td className="py-3 pr-4">
                      {scan.location_verified ? (
                        <CheckCircle size={16} className="text-green-500" />
                      ) : (
                        <XCircle size={16} className="text-gray-300" />
                      )}
                    </td>
                    <td className="py-3 text-sm text-gray-500">
                      {new Date(scan.scanned_at).toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* User Permissions Modal */}
      {showPermissions && (
        <UserPermissions
          userId={user.id}
          userEmail={user.email}
          onClose={() => setShowPermissions(false)}
        />
      )}
    </div>
  );
}
